/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { Injectable, Injector } from '@angular/core';
import { AlertContainerViewService, ViewService } from '@angular-kit/view';

import { TimeClockShellViewData, TimeClockShellViewModel } from './time-clock-shell.view-model';
import { TimeClockShellComponentModel } from '../component/time-clock-shell/time-clock-shell.component-model';
import { ActionModel, ContainerModel, Orientations, TextModel, ValueModel, ViewComponentKey } from '@typescript-kit/view';
import { TimeClockViewKey } from '../key/time-clock-view.key';
import { getMetaData } from '@angular-kit/core';
import { TimeClockService } from '../service/time-clock.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Alert, AlertKinds, ApplicationError, ChangedEvent, CoreErrorKey, MessageService, ObservableMap } from '@typescript-kit/core';
import { AuthenticationService } from '@angular-kit/authentication';
import { TimeClockFormatKey } from '@teamworks/global';
import { ShellViewService } from '../../shell/view/shell.view-service';
import { ShellGlobalTextKey } from '../../shell/key/shell-global-text.key';

@Injectable()
export class TimeClockShellViewService extends ViewService<TimeClockShellViewModel, TimeClockShellComponentModel> {
  private static readonly REFRESH_HOST_INFO_INTERVAL = 30 * 1000; // 30 seconds
  private static readonly REFRESH_DATE_TIME_INTERVAL = 1 * 1000; // 1 seconds
  private readonly router: Router;
  private readonly route: ActivatedRoute;
  private readonly messageService: MessageService;
  private readonly authenticationService: AuthenticationService;
  private readonly timeClockService: TimeClockService;
  private readonly shellViewService: ShellViewService;
  private readonly alertContainerViewService: AlertContainerViewService;
  private refreshDateTimeIntervalHandle: any;
  private refreshHostInfoIntervalHandle: any;

  constructor(injector: Injector) {
    super(injector);
    this.router = injector.get(Router);
    this.route = injector.get(ActivatedRoute);
    this.messageService = injector.get(MessageService);
    this.authenticationService = injector.get(AuthenticationService);
    this.timeClockService = injector.get(TimeClockService);
    this.shellViewService = injector.get(ShellViewService);
    this.alertContainerViewService = new AlertContainerViewService(injector);
  }

  initialize(data?: TimeClockShellViewData): void {
    const viewModel = (data && data instanceof TimeClockShellViewModel) ? data : new TimeClockShellViewModel(data);
    this.route.paramMap.subscribe((paramMap) => {
      viewModel.timeClockId = paramMap.get('id');
    });
    this.alertContainerViewService.initialize();
    super.initialize(viewModel);
    this.shellViewService.componentModel.alertContainer.isHidden = true;
    this.shellViewService.componentModel.isFooterVisible = false;
    this.startRefreshHostInfo();
    this.startRefreshDateTime();
    this.refreshRoute();
  }

  finalize(): void {
    this.stopRefreshHostInfo();
    this.stopRefreshDateTime();
    this.shellViewService.componentModel.alertContainer.isHidden = false;
    this.shellViewService.componentModel.isFooterVisible = true;
    super.finalize();
    this.alertContainerViewService.finalize();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.changes.serverVersion) {
      this.onServerVersionChanged(event);
    }
    if (event.changes.utcOffset || event.changes.timeOffset) {
      this.refreshDateTime();
    }
    if (event.changes.time) {
      this.onTimeChanged();
    }
    if (event.changes.nfcKey) {
      this.onNfcKeyChanged(event);
    }
  }

  private onServerVersionChanged(event: ChangedEvent) {
    const originalVersion: string = event.changes.serverVersion.originalValue;
    const version: string = event.changes.serverVersion.currentValue;
    if (originalVersion && originalVersion !== version) {
      window.location.reload(true);
      return;
    }
    this.refreshFooterModel(this.componentModel.footer);
  }

  private onTimeChanged() {
    this.refreshTimeModel(this.componentModel.time);
    this.refreshDateModel(this.componentModel.date);
  }

  private onNfcKeyChanged(event: ChangedEvent) {
    this.refreshRoute();
  }

  private startRefreshHostInfo() {
    if (this.refreshHostInfoIntervalHandle) {
      return;
    }
    this.refreshHostInfo();
    this.refreshHostInfoIntervalHandle = setInterval(
      () => this.refreshHostInfo(), TimeClockShellViewService.REFRESH_HOST_INFO_INTERVAL
    );
  }

  private stopRefreshHostInfo() {
    if (!this.refreshHostInfoIntervalHandle) {
      return;
    }
    clearInterval(this.refreshHostInfoIntervalHandle);
    this.refreshHostInfoIntervalHandle = null;
  }

  private refreshHostInfo() {
    this.authenticationService.signalActivity();
    this.timeClockService.loadHostInfo()
      .then((hostInfo) => {
        const serverTime = moment.parseZone(hostInfo.time);
        this.viewModel.setValues({
          serverVersion: hostInfo.version,
          utcOffset: serverTime.utcOffset(),
          timeOffset: serverTime.valueOf() - Date.now(),
        });
      })
      .catch((error) => {
        let errorMessage = this.messageService.getMessage(error);
        if (error instanceof ApplicationError) {
          if (error.key === CoreErrorKey.HTTP_ERROR) {
            if (error.arguments.error instanceof ApplicationError) {
              if (error.arguments.error.key === CoreErrorKey.NETWORK_ERROR) {
                errorMessage = this.textService.getText('time-clock/error/network-error');
              }
            }
          }
        }
        this.alertService.alert(new Alert({
          message: errorMessage,
          kind: AlertKinds.ERROR,
          timeout: 10000
        }));
      });
  }

  private startRefreshDateTime() {
    if (this.refreshDateTimeIntervalHandle) {
      return;
    }
    this.refreshDateTime();
    this.refreshDateTimeIntervalHandle = setInterval(
      () => this.refreshDateTime(), TimeClockShellViewService.REFRESH_DATE_TIME_INTERVAL
    );
  }

  private stopRefreshDateTime() {
    if (!this.refreshDateTimeIntervalHandle) {
      return;
    }
    clearInterval(this.refreshDateTimeIntervalHandle);
    this.refreshDateTimeIntervalHandle = null;
  }

  private refreshRoute() {
    if (!this.viewModel.nfcKey) {
      this.router.navigate(['idle'], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    } else {
      this.router.navigate(['employee', this.viewModel.nfcKey], { relativeTo: this.route, queryParamsHandling: 'preserve' });
    }
  }

  private refreshDateTime() {
    const now = moment(Date.now() + this.viewModel.timeOffset);
    if (this.viewModel.utcOffset !== now.utcOffset()) {
      now.utcOffset(this.viewModel.utcOffset, false);
    }
    this.viewModel.time = now.toISOString(true);
  }

  protected createComponentModel(): TimeClockShellComponentModel {
    return new TimeClockShellComponentModel({
      items: new ObservableMap({
        alertContainer: this.alertContainerViewService.componentModel,
        title: this.createTitleModel(),
        time: this.createTimeModel(),
        date: this.createDateModel(),
        footer: this.createFooterModel(),
      })
    });
  }

  private createTitleModel() {
    return new TextModel({
      key: 'title',
      text: `${TimeClockViewKey.SHELL}/title`
    });
  }

  private createTimeModel() {
    return new ValueModel({
      key: 'time',
      value: this.viewModel.time,
      format: TimeClockFormatKey.TIME
    });
  }

  private refreshTimeModel(model: ValueModel) {
    model.value = this.viewModel.time;
  }

  private createDateModel() {
    return new ValueModel({
      key: 'date',
      value: this.viewModel.time,
      format: TimeClockFormatKey.LONG_DATE
    });
  }

  private refreshDateModel(model: ValueModel) {
    model.value = this.viewModel.time;
  }

  private createFooterModel(): ContainerModel {
    const model = new ContainerModel({
      key: 'footer', orientation: Orientations.SIDE_BY_SIDE,
      items: [
        new TextModel({
          key: 'vendor', tags: ['app-vendor'],
          // text: '© #(time-recording/global/vendor-name)'
          text: `© #(${ShellGlobalTextKey.VENDOR})`
        }),
        // this.createKeyCardActionModel('nfc-1'),
        // this.createKeyCardActionModel('nfc-2'),
        // this.createKeyCardActionModel('nfc-3'),
        // this.createKeyCardActionModel('nfc-4'),
        // this.createKeyCardActionModel('nfc-5'),
        new ActionModel({
          key: 'version', tags: ['app-version'],
          type: ViewComponentKey.LINK,
          onClick: () => {
            window.location.reload(true);
          }
        }),
      ]
    });
    this.refreshFooterModel(model);
    return model;
  }

  private refreshFooterModel(model: ContainerModel) {
    const serverVersion = this.viewModel.serverVersion;
    const clientVersion = getMetaData('app-version');
    const versionModel = model.items.get('version') as ActionModel;
    versionModel.setValues({
      content: serverVersion !== clientVersion
        ? `v${serverVersion || '-'} / ${clientVersion || '-'}`
        : `v${serverVersion || '-'}`
    });
  }

  // private createKeyCardActionModel(key: string): ActionModel {
  //   return new ActionModel({
  //     tags: ['app-key-card'],
  //     type: ViewComponentKey.LINK,
  //     content: key,
  //     onClick: () => this.viewModel.nfcKey = key
  //   });
  // }

}
